import store from 'store/index';
import Api from 'services/Api';
import {promiseQueue} from 'services/helper';
import fatal from 'store/modules/fatal';
import lost from 'store/modules/lost';
import moment from "moment";

/**
 * Mutation Types
 *
 * @type {Object}
 */
const mutationTypes = {
    INITIALISE_CLAIM: 'INITIALISE_CLAIM',
    RESET_CLAIM: 'RESET_CLAIM',
    SET_CLAIM_ID: 'SET_CLAIM_ID',
    LOAD_CLAIM: 'LOAD_CLAIM',
    LOADING_CLAIM: 'LOADING_CLAIM',
    LOADED_CLAIM: 'LOADED_CLAIM',
    DELETED_CLAIM: 'DELETED_CLAIM',
    SET_CURRENT: 'SET_CURRENT',
    SET_COSTS: 'SET_COSTS'
};

/**
 * Available Claim Type Mapping
 *
 * @type {Object}
 */
export const claimTypes = {
    'day_one_only_lost_years': 'reserves/dayOneLost',
    'day_one_only_fatal_accident': 'reserves/dayOneFatal',
    'fatal_accident_dependency': 'fatal/dependency',
    'fatal_accident_estate': 'fatal/estate',
    'lost_years': 'lost',
    'personal_injury': 'personal',
};

/**
 * Available Claim Type Mapping
 *
 * @type {Object}
 */
export const humanClaimTypes = {
    'day_one_only_lost_years': 'Day One Assessment Only Lost Years',
    'day_one_only_fatal_accident': 'Day One Assessment Only Fatal Accident',
    'fatal_accident_dependency': 'Fatal Accident Dependency Claim',
    'fatal_accident_estate': 'Fatal Accident Estate Claim',
    'lost_years': 'Lost Years Claim',
    'personal_injury': 'Personal Injury Claim',
};


/**
 * Claim Type Routing.
 *
 * @type {Object}
 */
export const claimTypeRouting = {
    'fatal_accident_dependency': 'fatal::dependency.step-',
    'fatal_accident_estate': 'fatal::estate.step-',
    'lost_years': 'lost::step-',
    'personal_injury': 'personal::step-',
};

/**
 * Return the current claim type from the state.
 *
 * Same as the getter used in this file, just provided as a convenient constant,
 * and pulls the store style string out (rather than the snake case version)
 *
 * @return {string}
 */
export const currentClaimType = () => claimTypes[store.getters['claim/type']];

/**
 * Return the current human claim type from the state.
 *
 * @return {string}
 */
export const currentHumanClaimType = () => humanClaimTypes[store.getters['claim/type']];

/**
 * Return the current Claim Step Index.
 *
 * @return {int}
 */
export const currentClaimStepIndex = () => store.getters[currentClaimType() + '/step/currentStepIndex'];

/**
 * Returns the Claim Steps as an Array of Objects.
 *
 * Dirty but it's been a long day — refactor later. Aden promises.
 *
 * @param  {rootBeer} rootState
 * @param  {string} type
 * @return {}
 */
export const claimSteps = (rootState, type) => {
    if (type === 'day_one_only_lost_years' || type === 'day_one_only_fatal_accident') {
        return {};
    }

    if (type === 'fatal_accident_dependency') {
        return rootState['fatal']['dependency'].step.steps;
    }

    if (type === 'fatal_accident_estate') {
        return rootState['fatal']['estate'].step.steps;
    }

    return rootState[claimTypes[type]].step.steps;
};

/**
 * Returns a string representation of an action to run.
 *
 * @param  {string} type
 * @param  {string} action
 *
 * @return {string}
 */
const onClaimType = (type, action) => {
    return claimTypes[type] + '/' + action;
};

/**
 * Sets the default request payload for a claim.
 *
 * @return {Object}
 */
export const requestPayload = (stepNo) => {
    return {
        // Set Claim Type, Id and Step Number
        claim_type: state.type,
        claim_id: state.id,
        step_number: stepNo,
        // Empty Data of Payload
        data: {},
    };
};

/**
 * Template for Loading Status
 *
 * @type {Object}
 */
const loadingTemplate = {
    id: null,
    type: null,
};

/**
 * Initial State
 *
 * @type {Object}
 */
const state = {
    // Claim Data
    id: null,
    type: null,
    current: null,

    // Action Status Flags
    loading: loadingTemplate,
};

/**
 * Getters
 *
 * @type {Object}
 */
const getters = {
    id: state => state.id,
    type: state => state.type,
    loading: state => state.loading,
    current: state => state.current
};

/**
 * Actions
 *
 * @type {Object}
 */
const actions = {
    /**
     * Initialises a new Claim Type
     */
    initialise({commit}, type) {
        commit(mutationTypes.INITIALISE_CLAIM, type);
    },
    /**
     * Attempts to save a given step of the current claim.
     */
    save({rootGetters, dispatch}, stepNo) {
        store
            .dispatch(onClaimType(state.type, 'save'), requestPayload(stepNo))
            .then((response) => {
                if (!rootGetters['claim/id'] && typeof response !== 'undefined') {
                    dispatch('claim/setClaimId', response.step.claim_id, {root: true});
                }
            });
    },
    /**
     * Set the Claim ID
     */
    setClaimId({commit, dispatch, state}, id) {
        commit(mutationTypes.SET_CLAIM_ID, {id});
        if (id != null && !state.current) {
            return dispatch('loadById', id);
        }
    },
    /**
     * Attempts to load a claim from the backend into the state.
     */
    load({state, dispatch, commit, rootState}, {claim, router, type}) {
        /**
         * Steps for fatal/lost
         * @param response
         */
        const processSteppedClaim = (response) => {
            return promiseQueue(
                response.steps,
                step => dispatch('loadStep', step)
            )
                .then(() => promiseQueue(
                    claimSteps(rootState, state.type),
                    step => dispatch('processStep', step)
                ))
                // Trigger Validation of Claim Steps
                .then(() => promiseQueue(
                    claimSteps(rootState, state.type),
                    step => dispatch('validateStep', step)
                ))
                // Now perform any route updates if required.
                .then(() => {
                    // Named route to navigate to.
                    let goTo = claimTypeRouting[state.type] + '1';

                    // Check if we are entering a new route or an existing one
                    if (
                        router.app.$route.params.id == state.id &&
                        router.app.$route.name == goTo
                    ) {
                        return;
                    }

                    // Push the route if we aren't already on it
                    router.push({name: goTo, params: state});
                })
                // Finally, update the menu
                .then(() => {
                    dispatch('documents/resetDocuments', null, {root: true});
                    dispatch('menu/updateMenu', true, {root: true});
                    dispatch('menu/updateSubMenu', claimTypes[state.type], {root: true});
                })
        };

        /**
         * Steps for a
         * @param response
         */
        const processDayOneFatalClaim = response => dispatch('reserves/dayOneFatal/reset', null, {root: true})
            .then(() => router.push('/reserving/day-one-assessment/fatal'))
            .then(() => dispatch('reserves/dayOneFatal/load', response, {root: true}));

        const processDayOneLostClaim = response => dispatch('reserves/dayOneLost/reset', null, {root: true})
            .then(() => router.push('/reserving/day-one-assessment/lost'))
            .then(() => dispatch('reserves/dayOneLost/load', response, {root: true}));

        // Reset any existing claim
        dispatch('reset')
            // Commit to state that we are loading a claim
            .then(() => commit(mutationTypes.LOADING_CLAIM, claim))
            // Load the Claim
            .then(() => Api.claims.load(claim.id))
            // Commit that we loaded the claim
            .then((response) => {
                commit(mutationTypes.LOAD_CLAIM);
                return response;
            })
            // Update the claim to include the response ID and type
            // since the type could have been missing if loading
            // from a dynamic route rather than from the dash.
            .then((response) => {
                // Setup our payload
                claim = {id: response.id, type: response.type};
                dispatch('setCurrent', response);
                commit(mutationTypes.INITIALISE_CLAIM, type ? type : response.type);
                // Determine next steps for claim
                let prom = null;
                switch (type ? type : response.type) {
                    case "day_one_only_lost_years":
                        prom = processDayOneLostClaim(response);
                        break;
                    case "day_one_only_fatal_accident":
                        prom = processDayOneFatalClaim(response);
                        break;
                    default:
                        prom = processSteppedClaim(response);
                }
                // Commit that the loaded claim action is complete
                return prom.then(() => commit(mutationTypes.LOADED_CLAIM, claim));
            });
    },
    /**
     * Resets all steps on all claim types.
     */
    reset({state, commit, dispatch, rootState}) {
        if (!state.type || !state.id) {
            dispatch('dropdown/close', null, {root: true});
            return;
        }

        if (state.type === "day_one_only_lost_years" || state.type === "day_one_only_fatal_accident") {
            return commit(mutationTypes.RESET_CLAIM, null);
        }

        let allTheSteps = _.map(claimTypes, (key, type) => {
            return _.map(claimSteps(rootState, type), (step) => {
                return {type, step}
            });
        });

        return promiseQueue(
            _.flatten(allTheSteps),
            ({type, step}) => dispatch('resetServerStep', {type, step})
        ).then(() => {
            commit(mutationTypes.RESET_CLAIM, null);
            dispatch('documents/resetDocuments', null, {root: true});
        });
    },
    /**
     * Dispatches off to a specific claim type, with a step as the payload so
     * that it can process data from the server and persist to the store.
     */
    loadStep({state, dispatch, getters}, step) {
        dispatch(onClaimType(state.loading.type || state.type, 'load'), step, {root: true});
    },
    /**
     * Dispatches off to a specific claim type, with a step as the payload so
     * that it can perform any post processing requirements. This could
     * include calculations, refreshing of data sources or anything else really...
     */
    processStep({state, dispatch, getters}, step) {
        dispatch(onClaimType(state.type, 'process'), step, {root: true});
    },
    /**
     * Dispatches off to a specific claim type, with a step as the payload so
     * that it can perform validation of the state. This isn't the most ideal
     * solution, but it's an improvement on what we had before...
     *
     * Really we should be validating before we commit to the state, not
     * afterwards. Aim to eventually use this to validate data coming back
     * from the server side, but improving our actions and mutations
     * to ensure values are validated before entering the state.
     */
    validateStep({state, dispatch, getters}, step) {
        dispatch(onClaimType(state.type, 'validate'), step, {root: true});
    },
    /**
     * Dispatches off to a specific claim type, with a step as the payload requesting
     * that it resets itself. The step then commences it's teardown process
     * before committing to the state that it has reset its data back to the defaults.
     */
    resetServerStep({state, dispatch, getters}, {type, step}) {
        dispatch(onClaimType(type, 'reset'), step, {root: true});
    },
    /**
     * Attempts to delete the specified claim.
     */
    delete({state, commit, dispatch, rootState}, {claim}) {
        return Api.claims.delete(claim.id);
    },
    setCurrent({commit}, claim) {
        commit(mutationTypes.SET_CURRENT, claim);
    },
    updateClaim({dispatch}, data) {
        return Api.claims.update(data);
    },
    updateCosts({commit}, data) {
        return Api.claims.updateCosts(data).then((response) => {
            commit(mutationTypes.SET_COSTS, response.cost);
        });
    },
    loadById({dispatch}, claimId) {
        return Api.claims.load(claimId).then(claim => dispatch('setCurrent', claim));
    }
};

/**
 * Mutations
 *
 * @type {Object}
 */
const mutations = {
    [mutationTypes.INITIALISE_CLAIM](state, payload) {
        state.type = payload;
    },
    [mutationTypes.RESET_CLAIM](state) {
        state.id = null;
        state.type = null;
        state.current = null;
    },
    [mutationTypes.SET_CLAIM_ID](state, payload) {
        state.id = payload.id;
    },
    [mutationTypes.LOAD_CLAIM](state, payload) {
        if (!payload) {
            state.id = state.loading.id;
            state.type = state.loading.type;
            return;
        }

        state.id = payload.id;
        state.type = payload.type || state.type;
    },
    [mutationTypes.LOADING_CLAIM](state, payload) {
        state.loading = payload;
    },
    [mutationTypes.LOADED_CLAIM](state, payload) {
        // Set Claim Loaded
        state.id = payload.id;
        state.type = payload.type;

        // Set Loading Complete
        state.loading.id = null;
        state.loading.type = null;
    },
    [mutationTypes.SET_CURRENT](state, claim) {
        state.current = claim;
    },
    [mutationTypes.SET_COSTS](state, cost) {
        state.current.cost = cost;
    }
};

/**
 *
 */
export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
    modules: {},
};



// WEBPACK FOOTER //
// ./src/store/modules/claim.js